import React, { useEffect } from 'react';
import { ModalProps } from '../../../types/modal';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    Link,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PricingTemplate } from '../../../types/contractor';
import FormTextField from '../../../shared/FormTextField';
import PricingTemplateItems from './PricingTemplateItems';
import {
    useCreatePricingTemplateMutation,
    useUpdatePricingTemplateMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import toast from '../../../shared/Toast';
import FormCheckbox from '../../../shared/FormCheckbox';
import { Close, HelpOutline } from '@mui/icons-material';
import CutoffInput from './CutoffInput';

const Schema = z.object({
    name: z.string().min(1, { message: 'Required' }),
    display: z.boolean().default(true),
    items: z
        .array(
            z.object({
                id: z.number().optional(),
                name: z.string().min(1, { message: 'Required' }),
                description: z.string(),
                cost: z.coerce
                    .number()
                    .multipleOf(0.01, { message: 'Invalid currency format.' })
                    .min(1, { message: 'At least A$1.00 required' }),
                cutoff: z
                    .number()
                    .min(0, { message: 'Min cutoff is 0 days' })
                    .max(7, { message: 'Max cutoff is 7 days' })
                    .nullable(),
            }),
        )
        .min(1, { message: 'At least 1 item required' }),
    order: z.number(),
    cutoff: z
        .number()
        .min(0, { message: 'Min cutoff is 0 days' })
        .max(7, { message: 'Max cutoff is 7 days' })
        .nullable(),
});

interface PricingTemplateModalProps extends ModalProps {
    template: PricingTemplate | null;
    length: number;
}

const PricingTemplateModal = ({
    template,
    open,
    length,
    onClose,
}: PricingTemplateModalProps) => {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const defaultValues = {
        name: '',
        display: true,
        items: [],
        order: length,
        cutoff: null,
    };

    const methods = useForm<PricingTemplate>({
        defaultValues,
        resolver: zodResolver(Schema),
    });

    const [createTemplate] = useCreatePricingTemplateMutation();
    const [updateTemplate] = useUpdatePricingTemplateMutation();

    const { control, handleSubmit, reset } = methods;

    const onSubmit = async (data: z.infer<typeof Schema>) => {
        try {
            if (!loggedInUser?.company) return;

            if (template?.id !== undefined) {
                await updateTemplate([
                    loggedInUser?.company,
                    template.id,
                    data,
                ]).unwrap();
            } else {
                await createTemplate([loggedInUser?.company, data]).unwrap();
            }

            toast.success('Template saved successfully');
            handleClose();
        } catch {
            toast.error('Error creating template');
        }
    };

    const handleClose = () => {
        onClose();
        reset(defaultValues);
    };

    useEffect(() => {
        if (template?.id !== undefined) {
            reset({
                ...template,
                cutoff: template.cutoff,
                items: template.items.map((item) => ({
                    ...item,
                    cutoff: item.cutoff,
                })),
            });
        } else {
            reset(defaultValues);
        }
    }, [template, open, length, reset]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={isMobile ? false : 'md'}
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        Pricing Template
                        <IconButton
                            onClick={handleClose}
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            overflow: 'auto',
                            maxHeight: '600px',
                        }}
                    >
                        <Stack spacing={2}>
                            <Box>
                                <Alert severity='info'>
                                    <Link
                                        href='https://help.7-day.com/hc/en-us/articles/14552013259407-Create-a-Pricing-Template'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        View pricing template guide{' '}
                                        <HelpOutline
                                            sx={{ fontSize: '14px' }}
                                        />
                                    </Link>
                                </Alert>
                            </Box>
                            <Typography
                                variant='subtitle1'
                                sx={{ fontWeight: 600, mb: 1 }}
                            >
                                Template Details
                            </Typography>
                            <div>
                                <InputLabel
                                    required
                                    htmlFor='template-name__input'
                                >
                                    Template Name
                                </InputLabel>
                                <FormTextField
                                    id='template-name__input'
                                    name='name'
                                    control={control}
                                    placeholder='Service pricing list'
                                />
                                <FormHelperText>
                                    Give this template a clear, descriptive
                                    name.
                                </FormHelperText>
                            </div>
                            <div>
                                <InputLabel htmlFor='template-cutoff__input'>
                                    Job request cut-off
                                </InputLabel>
                                <CutoffInput
                                    id='template-cutoff__input'
                                    name='cutoff'
                                    options={[
                                        { value: 0, label: 'Same day' },
                                        {
                                            value: 1,
                                            label: '1 business day',
                                        },
                                        {
                                            value: 2,
                                            label: '2 business days',
                                        },
                                        {
                                            value: 3,
                                            label: '3 business days',
                                        },
                                        {
                                            value: 4,
                                            label: '4 business days',
                                        },
                                        {
                                            value: 5,
                                            label: '5 business days',
                                        },
                                        {
                                            value: 6,
                                            label: '6 business days',
                                        },
                                        {
                                            value: 7,
                                            label: '7 business days',
                                        },
                                    ]}
                                    placeholder='Same as organisation'
                                />
                                <FormHelperText>
                                    Minimum business days before the job start
                                    date that requests must be submitted, based
                                    on your available days.
                                </FormHelperText>
                            </div>
                            <Box>
                                <Typography
                                    variant='subtitle1'
                                    sx={{ fontWeight: 600, mb: 1 }}
                                >
                                    Visibility
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <FormCheckbox
                                            id='display__input'
                                            name='display'
                                            control={control}
                                        />
                                    }
                                    label='Public'
                                    htmlFor='display__input'
                                    sx={{ px: 1 }}
                                />
                                <FormHelperText>
                                    Turn this off to keep the template for
                                    internal use only.
                                </FormHelperText>
                            </Box>
                            <Box>
                                <Typography
                                    variant='subtitle1'
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Services in this template
                                </Typography>
                                <FormHelperText>
                                    Add the services and prices that belong to
                                    this template.
                                </FormHelperText>
                            </Box>
                            <PricingTemplateItems />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <CustomButton
                            color='error'
                            type='button'
                            variant='contained'
                            onClick={handleClose}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            type='submit'
                            variant='contained'
                            color='primary'
                        >
                            Save
                        </CustomButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default PricingTemplateModal;
